import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShieldCheck, Cpu, Save, Sparkles, Gift, BarChart3, HardDrive, AlertTriangle, UserPlus, Trash2, Power, Mail, KeyRound, Copy, Check, Activity, LogIn, ScanLine, FileText, Send, QrCode, Users, Clock, Mic } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  const days = Math.floor(s / 86400);
  if (days < 7) return `il y a ${days} j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}
const EV_META = {
  login: { icon: LogIn, label: "Connexion", c: "text-blue-600", bg: "bg-blue-500/10" },
  scan: { icon: ScanLine, label: "Scan de carte", c: "text-violet-600", bg: "bg-violet-500/10" },
  lead_created: { icon: FileText, label: "Fiche créée", c: "text-emerald-600", bg: "bg-emerald-500/10" },
  debrief: { icon: Mic, label: "Débrief enregistré", c: "text-teal-600", bg: "bg-teal-500/10" },
  email: { icon: Send, label: "E-mail envoyé", c: "text-sky-600", bg: "bg-sky-500/10" },
  capture: { icon: QrCode, label: "Capture QR", c: "text-amber-600", bg: "bg-amber-500/10" },
};
import { toast } from "sonner";

const TASK_META = {
  ocr: { icon: "🪪", hint: "Extraction des cartes de visite. Peu sensible : un modèle économique suffit." },
  analysis: { icon: "🎙️", hint: "Résumé et scoring du débrief. Bon compromis en modèle économique." },
  synthesis: { icon: "🧠", hint: "Consolidation multi-rencontres : le premium apporte le plus de valeur ici." },
  email: { icon: "✉️", hint: "Rédaction des relances. Un modèle économique reste convaincant." },
};

const MODEL_TAG = {
  "gpt-4o": { label: "Premium", cls: "bg-violet-500/10 text-violet-600 dark:text-violet-400" },
  "gpt-4.1": { label: "Premium", cls: "bg-violet-500/10 text-violet-600 dark:text-violet-400" },
  "gpt-5.4": { label: "Premium +", cls: "bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400" },
  "gpt-4.1-mini": { label: "Économique", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  "gpt-5.4-mini": { label: "Économique", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
};

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowed, setAllowed] = useState([]);
  const [labels, setLabels] = useState({});
  const [models, setModels] = useState({});
  const [defaults, setDefaults] = useState({});
  const [trialDays, setTrialDays] = useState(7);
  const [savingTrial, setSavingTrial] = useState(false);
  const [usage, setUsage] = useState(null);
  const [budget, setBudget] = useState(0);
  const [savingBudget, setSavingBudget] = useState(false);
  const [storage, setStorage] = useState(null);
  const [retentionDays, setRetentionDays] = useState(0);
  const [storageBudgetMb, setStorageBudgetMb] = useState(0);
  const [savingStorage, setSavingStorage] = useState(false);
  const [freeAccounts, setFreeAccounts] = useState([]);
  const [newFreeEmail, setNewFreeEmail] = useState("");
  const [newFreeName, setNewFreeName] = useState("");
  const [newFreePassword, setNewFreePassword] = useState("");
  const [freeBusy, setFreeBusy] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [copied, setCopied] = useState("");

  const [activity, setActivity] = useState(null);
  const [feed, setFeed] = useState([]);
  const loadFree = () => api.get("/admin/free-accounts").then(({ data }) => setFreeAccounts(data || [])).catch(() => {});
  const loadActivity = () => {
    api.get("/admin/activity/summary").then(({ data }) => setActivity(data)).catch(() => {});
    api.get("/admin/activity/feed?limit=40").then(({ data }) => setFeed(data.events || [])).catch(() => {});
  };
  useEffect(() => { if (user?.isPlatformAdmin) { loadFree(); loadActivity(); } }, [user]);
  const copyText = (key, text) => { navigator.clipboard?.writeText(text); setCopied(key); toast.success("Copié ✓"); setTimeout(() => setCopied(""), 1500); };
  const addFree = async () => {
    const email = newFreeEmail.trim().toLowerCase();
    if (!email) return;
    const password = newFreePassword.trim();
    if (password && password.length < 6) { toast.error("Le mot de passe doit contenir au moins 6 caractères."); return; }
    setFreeBusy(true);
    try {
      await api.post("/admin/free-accounts", { email, password: password || undefined, name: newFreeName.trim() || undefined });
      if (password) {
        setCreatedInfo({ email, password, link: `${window.location.origin}/login` });
        toast.success("Compte créé — envoie le lien à ton ami ✨");
      } else {
        setCreatedInfo(null);
        toast.success("Accès à vie accordé ✨");
      }
      setNewFreeEmail(""); setNewFreeName(""); setNewFreePassword("");
      await loadFree();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Email invalide"); }
    finally { setFreeBusy(false); }
  };
  const toggleFree = async (r) => { try { await api.patch("/admin/free-accounts", { email: r.email, active: !r.active }); await loadFree(); } catch { toast.error("Échec"); } };
  const delFree = async (r) => { try { await api.delete(`/admin/free-accounts/${encodeURIComponent(r.email)}`); await loadFree(); } catch { toast.error("Échec"); } };

  useEffect(() => {
    if (user && !user.isPlatformAdmin) { navigate("/", { replace: true }); return; }
    Promise.all([api.get("/admin/ai-config"), api.get("/admin/settings"), api.get("/admin/ai-usage"), api.get("/admin/storage")])
      .then(([cfg, st, us, stg]) => {
        setAllowed(cfg.data.allowed); setLabels(cfg.data.labels); setModels(cfg.data.models); setDefaults(cfg.data.defaults);
        setTrialDays(st.data.trial_days);
        setBudget(st.data.ai_budget_usd ?? 0);
        setRetentionDays(st.data.audio_retention_days ?? 0);
        setStorageBudgetMb(st.data.storage_budget_mb ?? 0);
        setUsage(us.data);
        setStorage(stg.data);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Accès refusé"))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const saveStorageConfig = async () => {
    setSavingStorage(true);
    try {
      await api.put("/admin/storage-config", { audio_retention_days: Number(retentionDays), storage_budget_mb: Number(storageBudgetMb) });
      const stg = await api.get("/admin/storage");
      setStorage(stg.data);
      toast.success("Réglages de stockage enregistrés ✨");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSavingStorage(false); }
  };

  const saveTrial = async () => {
    setSavingTrial(true);
    try {
      const { data } = await api.put("/admin/settings", { trial_days: Number(trialDays) });
      setTrialDays(data.trial_days);
      toast.success("Durée d'essai enregistrée ✨");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSavingTrial(false); }
  };

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      await api.put("/admin/budget", { ai_budget_usd: Number(budget) });
      const us = await api.get("/admin/ai-usage");
      setUsage(us.data);
      toast.success("Seuil d'alerte budget enregistré ✨");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSavingBudget(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/ai-config", models);
      setModels(data.models);
      toast.success("Configuration IA enregistrée ✨");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const resetDefaults = () => setModels({ ...defaults });

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-primary klt-spin" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-primary" />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Administration plateforme</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Modèles IA & coûts</h1>
        </div>
      </div>

      {activity && (
        <section className="mt-6 rounded-2xl border border-emerald-500/25 bg-card p-5 shadow-sm" data-testid="activity-section">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/12 flex items-center justify-center"><Activity className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <h2 className="font-heading font-bold text-lg">Suivi d'activité</h2>
              <p className="text-xs text-muted-foreground max-w-xl">Qui se connecte, combien de fois, scans, fiches, e-mails, captures… Le comptage démarre à l'activation ; les fiches sont comptées rétroactivement.</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {[
              { k: "activeUsers", label: "Utilisateurs actifs", icon: Users, c: "text-emerald-600" },
              { k: "logins", label: "Connexions", icon: LogIn, c: "text-blue-600" },
              { k: "scans", label: "Scans", icon: ScanLine, c: "text-violet-600" },
              { k: "leads", label: "Fiches", icon: FileText, c: "text-emerald-600" },
              { k: "emails", label: "E-mails", icon: Send, c: "text-sky-600" },
              { k: "captures", label: "Captures QR", icon: QrCode, c: "text-amber-600" },
            ].map((s) => { const I = s.icon; return (
              <div key={s.k} data-testid={`stat-${s.k}`} className="rounded-xl border border-border bg-secondary/30 p-3">
                <I className={`w-4 h-4 ${s.c}`} />
                <div className="text-2xl font-extrabold tabular-nums mt-1">{activity.totals?.[s.k] ?? 0}</div>
                <div className="text-[11px] text-muted-foreground">{s.label}</div>
              </div>); })}
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm min-w-[620px]" data-testid="activity-users-table">
              <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2 font-medium">Utilisateur</th>
                <th className="text-right font-medium px-2">Dern. connexion</th>
                <th className="text-right font-medium px-2">Connexions</th>
                <th className="text-right font-medium px-2">Scans</th>
                <th className="text-right font-medium px-2">Fiches</th>
                <th className="text-right font-medium px-2">Débriefs</th>
                <th className="text-right font-medium px-2">E-mails</th>
                <th className="text-right font-medium px-2">Captures</th>
              </tr></thead>
              <tbody>
                {activity.users.slice(0, 50).map((u) => (
                  <tr key={u.userId} data-testid={`activity-user-${u.userId}`} className="border-b border-border/50 hover:bg-secondary/30">
                    <td className="py-2">
                      <div className="font-medium truncate max-w-[220px]">{u.name}
                        {u.isAdmin && <span className="ml-1.5 text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-primary/10 text-primary align-middle">admin</span>}
                        {u.freeForever && <span className="ml-1.5 text-[9px] font-bold uppercase rounded px-1 py-0.5 bg-emerald-500/10 text-emerald-600 align-middle">à vie</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{u.email}</div>
                    </td>
                    <td className="text-right text-xs text-muted-foreground whitespace-nowrap px-2">{timeAgo(u.lastLogin)}</td>
                    <td className="text-right tabular-nums font-semibold px-2">{u.logins}</td>
                    <td className="text-right tabular-nums px-2">{u.scans}</td>
                    <td className="text-right tabular-nums px-2">{u.leads}</td>
                    <td className="text-right tabular-nums px-2">{u.debriefs}</td>
                    <td className="text-right tabular-nums px-2">{u.emails}</td>
                    <td className="text-right tabular-nums px-2">{u.captures}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5">
            <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Activité récente</h3>
            {feed.length === 0 ? (<p className="text-sm text-muted-foreground" data-testid="activity-feed-empty">Aucune activité enregistrée pour l'instant.</p>) : (
              <div className="space-y-1.5" data-testid="activity-feed">
                {feed.map((e, i) => { const m = EV_META[e.type] || { icon: Activity, label: e.type, c: "text-muted-foreground", bg: "bg-secondary" }; const I = m.icon; return (
                  <div key={i} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2">
                    <span className={`w-7 h-7 rounded-lg ${m.bg} flex items-center justify-center shrink-0 ${m.c}`}><I className="w-3.5 h-3.5" /></span>
                    <div className="flex-1 min-w-0 truncate"><span className="text-sm font-medium">{m.label}</span> <span className="text-xs text-muted-foreground">· {e.name || e.email}</span></div>
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">{timeAgo(e.at)}</span>
                  </div>); })}
              </div>
            )}
          </div>
        </section>
      )}

      <div className="mt-4 rounded-2xl border border-primary/20 bg-primary/5 p-4 text-sm text-muted-foreground flex gap-3">
        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <p>Choisissez le modèle utilisé pour chaque tâche IA. <b className="text-foreground">Premium</b> = meilleure qualité (coût plus élevé), <b className="text-foreground">Économique</b> = ~90-95 % de la qualité pour une fraction du coût. La transcription vocale reste sur un modèle de qualité (non modifiable ici) pour ne pas dégrader tout le reste.</p>
      </div>

      <div className="mt-6 space-y-4">
        {Object.keys(labels).map((task) => (
          <div key={task} className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid={`ai-task-${task}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <span className="text-2xl leading-none mt-0.5">{TASK_META[task]?.icon}</span>
                <div>
                  <h3 className="font-heading font-bold text-base">{labels[task]}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5 max-w-md">{TASK_META[task]?.hint}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {MODEL_TAG[models[task]] && (
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${MODEL_TAG[models[task]].cls}`}>{MODEL_TAG[models[task]].label}</span>
                )}
                <Select value={models[task]} onValueChange={(v) => setModels((m) => ({ ...m, [task]: v }))}>
                  <SelectTrigger className="w-[168px] rounded-xl" data-testid={`ai-select-${task}`}>
                    <Cpu className="w-4 h-4 mr-1 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allowed.map((m) => (
                      <SelectItem key={m} value={m} data-testid={`ai-option-${task}-${m}`}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button onClick={save} disabled={saving} data-testid="admin-save-button" className="rounded-xl">
          {saving ? <Loader2 className="w-4 h-4 mr-2 klt-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
        </Button>
        <Button onClick={resetDefaults} variant="outline" data-testid="admin-reset-button" className="rounded-xl">
          Réinitialiser (tout Premium)
        </Button>
      </div>

      <div className="mt-10 rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="admin-trial-card">
        <div className="flex items-start gap-3">
          <span className="text-2xl leading-none mt-0.5"><Gift className="w-6 h-6 text-emerald-500" /></span>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-base">Période d'essai gratuite</h3>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-lg">Nombre de jours d'accès gratuit pour une nouvelle entreprise sans abonnement actif. Passé ce délai, l'écran « Abonnement requis » réapparaît. L'essai est compté <b className="text-foreground">par entreprise</b>.</p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Input type="number" min={0} value={trialDays} onChange={(e) => setTrialDays(e.target.value)} className="w-28 rounded-xl" data-testid="trial-days-input" />
          <span className="text-sm text-muted-foreground">jours</span>
          <Button onClick={saveTrial} disabled={savingTrial} data-testid="trial-save-button" className="rounded-xl">
            {savingTrial ? <Loader2 className="w-4 h-4 mr-2 klt-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
          </Button>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="admin-usage-card">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-primary" />
          <div>
            <h3 className="font-heading font-bold text-base">Consommation IA</h3>
            <p className="text-xs text-muted-foreground">Coût IA estimé par entreprise et par mois (estimation basée sur les tarifs des modèles).</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-secondary/50 p-3" data-testid="usage-current-month">
            <div className="text-xs text-muted-foreground">Mois en cours ({usage?.currentMonth || "—"})</div>
            <div className="text-xl font-extrabold text-primary">${(usage?.currentMonthTotalUsd ?? 0).toFixed(2)}</div>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3" data-testid="usage-all-time">
            <div className="text-xs text-muted-foreground">Total cumulé</div>
            <div className="text-xl font-extrabold">${(usage?.allTimeTotalUsd ?? 0).toFixed(2)}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="budget-control">
          <span className="text-sm text-muted-foreground">Seuil d'alerte / entreprise / mois :</span>
          <div className="flex items-center gap-1">
            <span className="text-sm">$</span>
            <Input type="number" min={0} step="0.5" value={budget} onChange={(e) => setBudget(e.target.value)} className="w-28 rounded-xl" data-testid="budget-input" />
          </div>
          <Button onClick={saveBudget} disabled={savingBudget} data-testid="budget-save-button" className="rounded-xl" size="sm">
            {savingBudget ? <Loader2 className="w-4 h-4 mr-2 klt-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer le seuil
          </Button>
          <span className="text-xs text-muted-foreground w-full sm:w-auto">0 = désactivé. Un email est envoyé à l'admin au dépassement.</span>
        </div>
        {usage?.overBudgetCompanies?.length ? (
          <div className="mt-3 rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-300" data-testid="budget-alert-banner">
            ⚠️ {usage.overBudgetCompanies.length} entreprise(s) au-dessus du seuil ce mois-ci : {usage.overBudgetCompanies.map((o) => `${o.company} ($${o.costUsd.toFixed(2)})`).join(", ")}
          </div>
        ) : null}
        {usage?.rows?.length ? (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="usage-table">
              <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2 font-medium">Mois</th><th className="font-medium">Entreprise</th>
                <th className="text-right font-medium">Appels</th><th className="text-right font-medium">Coût est.</th>
              </tr></thead>
              <tbody>
                {usage.rows.map((r, i) => (
                  <tr key={i} className={`border-b border-border/50 ${r.overBudget ? "bg-rose-500/10" : ""}`} data-testid={`usage-row-${i}`}>
                    <td className="py-2 font-mono text-xs">{r.month}</td>
                    <td className="font-medium">{r.company}</td>
                    <td className="text-right tabular-nums">{r.calls}</td>
                    <td className="text-right tabular-nums font-semibold">${r.costUsd.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="usage-empty">Aucune consommation IA enregistrée pour le moment.</p>
        )}
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="admin-storage-card">
        <div className="flex items-center gap-3">
          <HardDrive className="w-6 h-6 text-primary" />
          <div>
            <h3 className="font-heading font-bold text-base">Stockage (audio des débriefs)</h3>
            <p className="text-xs text-muted-foreground">Les fichiers audio sont le principal poste de coût. Le texte (résumé, synthèse, score) est toujours conservé.</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-secondary/50 p-3" data-testid="storage-total">
            <div className="text-xs text-muted-foreground">Stockage audio total</div>
            <div className="text-xl font-extrabold text-primary">{(storage?.totalMb ?? 0).toFixed(2)} Mo</div>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3" data-testid="storage-files">
            <div className="text-xs text-muted-foreground">Fichiers audio</div>
            <div className="text-xl font-extrabold">{storage?.totalFiles ?? 0}</div>
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Rétention audio (jours)</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input type="number" min={0} value={retentionDays} onChange={(e) => setRetentionDays(e.target.value)} className="w-28 rounded-xl" data-testid="retention-days-input" />
              <span className="text-xs text-muted-foreground">0 = illimité</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Au-delà, l'audio est purgé automatiquement (texte conservé).</p>
          </div>
          <div>
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Seuil d'alerte stockage / entreprise (Mo)</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input type="number" min={0} step="10" value={storageBudgetMb} onChange={(e) => setStorageBudgetMb(e.target.value)} className="w-32 rounded-xl" data-testid="storage-budget-input" />
              <span className="text-xs text-muted-foreground">0 = désactivé</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Les managers voient une suggestion d'archivage au dépassement.</p>
          </div>
        </div>
        <Button onClick={saveStorageConfig} disabled={savingStorage} data-testid="storage-save-button" className="mt-4 rounded-xl" size="sm">
          {savingStorage ? <Loader2 className="w-4 h-4 mr-2 klt-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer les réglages de stockage
        </Button>
        {storage?.rows?.length ? (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="storage-table">
              <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2 font-medium">Entreprise</th><th className="text-right font-medium">Audio (Mo)</th>
                <th className="text-right font-medium">Fichiers</th><th className="text-right font-medium">Leads archivés</th>
              </tr></thead>
              <tbody>
                {storage.rows.map((r, i) => (
                  <tr key={i} className={`border-b border-border/50 ${r.overBudget ? "bg-rose-500/10" : ""}`} data-testid={`storage-row-${i}`}>
                    <td className="py-2 font-medium">{r.company} {r.overBudget && <AlertTriangle className="inline w-3.5 h-3.5 text-rose-500 ml-1" />}</td>
                    <td className="text-right tabular-nums font-semibold">{r.mb.toFixed(2)}</td>
                    <td className="text-right tabular-nums">{r.files}</td>
                    <td className="text-right tabular-nums">{r.archivedLeads}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="storage-empty">Aucun fichier audio stocké pour le moment.</p>
        )}
      </div>
      <div className="mt-8 rounded-2xl border border-emerald-500/25 bg-card p-5 shadow-sm" data-testid="admin-free-card">
        <div className="flex items-center gap-3">
          <UserPlus className="w-6 h-6 text-emerald-500" />
          <div>
            <h3 className="font-heading font-bold text-base">Comptes test — accès à vie</h3>
            <p className="text-xs text-muted-foreground max-w-lg">Crée un compte pour un ami : renseigne un e-mail (même fictif, ex. <b className="text-foreground">ami@kallitag.fr</b>) + un mot de passe, et le compte est créé immédiatement avec <b className="text-foreground">accès gratuit à vie</b> (toutes les fonctions sauf cette page Admin). Envoie-lui simplement le lien de connexion. <span className="opacity-80">Le mot de passe est optionnel : sans mot de passe, l'e-mail est juste autorisé (la personne crée alors son compte elle-même).</span></p>
          </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <Input value={newFreeName} onChange={(e) => setNewFreeName(e.target.value)} placeholder="Nom (optionnel)" className="rounded-xl" data-testid="free-name-input" />
          <Input type="email" value={newFreeEmail} onChange={(e) => setNewFreeEmail(e.target.value)} placeholder="email@exemple.com" className="rounded-xl" data-testid="free-email-input" />
        </div>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <KeyRound className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input type="text" value={newFreePassword} onChange={(e) => setNewFreePassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addFree()} placeholder="Mot de passe (min. 6 car.)" className="rounded-xl pl-9" data-testid="free-password-input" />
          </div>
          <Button onClick={addFree} disabled={freeBusy || !newFreeEmail.trim()} className="rounded-xl" data-testid="free-add-button">{freeBusy ? <Loader2 className="w-4 h-4 mr-2 klt-spin" /> : <UserPlus className="w-4 h-4 mr-2" />} {newFreePassword.trim() ? "Créer le compte" : "Autoriser l'e-mail"}</Button>
        </div>
        {createdInfo && (
          <div className="mt-4 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="free-created-info">
            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-2"><Check className="w-4 h-4" /> Compte prêt — envoie ces infos à ton ami</p>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex items-center gap-2"><span className="w-24 shrink-0 text-muted-foreground">E-mail</span><code className="flex-1 truncate font-mono text-xs bg-background rounded px-2 py-1">{createdInfo.email}</code><button onClick={() => copyText("email", createdInfo.email)} className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-emerald-600" data-testid="copy-email-btn">{copied === "email" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button></div>
              <div className="flex items-center gap-2"><span className="w-24 shrink-0 text-muted-foreground">Mot de passe</span><code className="flex-1 truncate font-mono text-xs bg-background rounded px-2 py-1">{createdInfo.password}</code><button onClick={() => copyText("pw", createdInfo.password)} className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-emerald-600" data-testid="copy-pw-btn">{copied === "pw" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button></div>
              <div className="flex items-center gap-2"><span className="w-24 shrink-0 text-muted-foreground">Lien</span><code className="flex-1 truncate font-mono text-xs bg-background rounded px-2 py-1">{createdInfo.link}</code><button onClick={() => copyText("link", createdInfo.link)} className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-emerald-600" data-testid="copy-link-btn">{copied === "link" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button></div>
            </div>
            <Button variant="outline" size="sm" onClick={() => copyText("all", `Connecte-toi à Kallitag Lead Capture :\n${createdInfo.link}\nE-mail : ${createdInfo.email}\nMot de passe : ${createdInfo.password}`)} className="mt-3 rounded-xl" data-testid="copy-all-btn"><Copy className="w-4 h-4 mr-2" /> Copier le message complet</Button>
          </div>
        )}
        <div className="mt-4 space-y-2">
          {freeAccounts.length === 0 ? (<p className="text-sm text-muted-foreground" data-testid="free-empty">Aucun compte à vie pour le moment.</p>) :
            freeAccounts.map((r) => (
              <div key={r.email} data-testid={`free-row-${r.email}`} className="flex items-center gap-3 rounded-xl border border-border p-3">
                <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="flex-1 truncate text-sm font-medium">{r.email}{r.hasLogin && <span className="ml-2 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-blue-500/10 text-blue-600 align-middle">connexion active</span>}</span>
                <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${r.active ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"}`}>{r.active ? "Actif" : "Désactivé"}</span>
                <button onClick={() => toggleFree(r)} data-testid={`free-toggle-${r.email}`} title={r.active ? "Désactiver" : "Activer"} className={`p-1.5 rounded-lg transition-colors ${r.active ? "text-emerald-600 hover:bg-emerald-50" : "text-muted-foreground hover:bg-secondary"}`}><Power className="w-4 h-4" /></button>
                <button onClick={() => delFree(r)} data-testid={`free-delete-${r.email}`} className="p-1.5 rounded-lg text-muted-foreground/60 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
